import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { generateClient } from "@aws-amplify/api";
import { getRecruitmentSources } from "@/graphql/queries";
import useSubjectAPI from "./useSubjectAPI";

const client = generateClient();

const RecruitmentSourceSection = ({ subjectId, form }) => {
  const isCreate = !subjectId;

  const { loadSubject, saveSubjectDirect } = useSubjectAPI(subjectId);

  const [open, setOpen] = useState(true);
  const [availableSources, setAvailableSources] = useState([]);
  
  const [sourcesList, setSourcesList] = useState(
    isCreate ? [{ recruitment_id: "" }] : []
  );

  const [editingMap, setEditingMap] = useState({});
  
  
  const [originalValues, setOriginalValues] = useState({});

  const purple = "#2E2868";
  const pillBg = "#ffffff";

  /* LOAD OPTIONS */
  useEffect(() => {
    client
      .graphql({ query: getRecruitmentSources })
      .then((res) =>
        setAvailableSources(res.data?.getRecruitmentSources?.recruitment_sources || [])
      );
  }, []);

  /* LOAD DATA (Edit Mode) */
  useEffect(() => {
    if (isCreate) return;

    loadSubject().then((res) => {
      setSourcesList(res?.subject_recruitment_sources || []);
    });
  }, [subjectId, isCreate]);

  /* SYNC TO FORM (Create Mode Only) */
  useEffect(() => {
    if (!isCreate || !form?.setValue) return;

    form.setValue({
      key: "subject_recruitment_sources",
      value: sourcesList.map(s => ({
          recruitment_id: Number(s.recruitment_id) || null
      })),
    });
  }, [sourcesList, isCreate, form]);

  /* HANDLERS */
  const toggleEdit = (index, field, status) => {
    if (isCreate) return;
    const editKey = `${index}-${field}`;

    
    if (status === true) {
      setOriginalValues(prev => ({ ...prev, [editKey]: sourcesList[index]?.[field] }));
    }

    setEditingMap((prev) => ({ ...prev, [editKey]: status }));
  };

  // Added Cancel Handler
  const handleCancel = (index, field) => {
    const editKey = `${index}-${field}`;
    const originalValue = originalValues[editKey];

    const updated = [...sourcesList];
    updated[index] = { ...updated[index], [field]: originalValue };
    
    setSourcesList(updated);
    setEditingMap((prev) => ({ ...prev, [editKey]: false }));
  };

  const handleChange = (index, field, val) => {
    const value = Number(val);
    const updated = [...sourcesList];
    if(!updated[index]) updated[index] = {};
    
    updated[index] = { ...updated[index], [field]: value };
    setSourcesList(updated);
  };

  const saveField = async (index, field) => {
    const editKey = `${index}-${field}`;
    setEditingMap((prev) => ({ ...prev, [editKey]: false }));

    const payload = {
        subjects: { id: subjectId },
        subject_recruitment_sources: sourcesList.map(s => ({
            id: s.id, 
            subject_id: subjectId,
            recruitment_id: Number(s.recruitment_id) || null
        }))
    };

    try {
        const res = await saveSubjectDirect(payload);
        if (res?.subject_recruitment_sources) {
            setSourcesList(res.subject_recruitment_sources);
        }
    } catch (err) {
        alert("Save failed");
        console.error(err);
       
        handleCancel(index, field);
    }
  };

  const handleAdd = () => {
    setSourcesList(prev => [...prev, { recruitment_id: "" }]);
    setOpen(true);
  };

 const handleRemove = async (index) => {
    const sourceToRemove = sourcesList[index]; 
    if (!window.confirm("Remove this recruitment source?")) return;

    
    const newList = sourcesList.filter((_, i) => i !== index);
    setSourcesList(newList);

   
    if (!isCreate && sourceToRemove.id) {
      try {
        const payload = {
          subjects: { id: subjectId }
        };

       
        await saveSubjectDirect(payload, {
          input_delete: [
            { id: sourceToRemove.id, key: "subject_recruitment_sources" }
          ]
        });

        console.log("Deleted successfully from DB");
      } catch (err) {
        console.error("Delete failed", err);
        alert("Could not remove from database");
       
        setSourcesList(sourcesList); 
      }
    }
  };

  /* UI HELPER (Render Select) */
  const renderSelect = (index, label, field, options, value) => {
    const editKey = `${index}-${field}`;
    const isEditing = editingMap[editKey];
    const isInputActive = isCreate || isEditing;

    const viewStyle = {
      backgroundColor: pillBg,
      padding: "0.5rem 70px 0.5rem 0.5rem",
      cursor: "text",
      userSelect: "text",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      border: isCreate ? "1px solid #ced4da" : "none"
    };

    const inputStyle = {
      backgroundColor: isInputActive ? "#fff" : pillBg,
      border: isInputActive ? `2px solid ${purple}` : "0",
      boxShadow: isInputActive ? "0 0 5px rgba(46, 40, 104, 0.2)" : "none",
      // Increased right padding to accommodate Save and Cancel buttons
      padding: isInputActive
        ? "0.5rem 105px 0.5rem 0.5rem"
        : "0.5rem 2rem 0.5rem 0.5rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: isCreate ? "pointer" : "text",
    };

    const selectedOption = options.find(
      (o) => String(o.value) === String(value)
    );
    const displayValue = selectedOption ? selectedOption.label : value;

    return (
      <div className="mb-3">
        <label className="form-label mb-1 fw-bold" style={{ fontSize: "13px" }}>{label}</label>

        <div style={{ position: "relative" }}>
          
          {isCreate && (
            <select
              value={value || ""}
              onChange={(e) => handleChange(index, field, e.target.value)}
              className="form-select rounded-pill"
              style={inputStyle}
              title={displayValue}
            >
              <option value="">Select...</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          )}

          {!isCreate && !isEditing && (
            <>
              <div
                className="form-control rounded-pill border-0"
                style={viewStyle}
                title={displayValue}
              >
                {displayValue || "Select..."}
              </div>

              <FontAwesomeIcon
                icon={faPen}
                onClick={() => toggleEdit(index, field, true)}
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: purple,
                  fontSize: 14,
                  zIndex: 2,
                }}
              />
            </>
          )}

          {!isCreate && isEditing && (
            <>
              <select
                value={value || ""}
                onChange={(e) => handleChange(index, field, e.target.value)}
                className="form-select rounded-pill"
                style={inputStyle}
                autoFocus
              >
                <option value="">Select...</option>
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", zIndex: 2, display: "flex", gap: "4px" }}>
                <button
                    onClick={() => saveField(index, field)}
                    className="btn btn-sm"
                    style={{ background: purple, color: "#fff", borderRadius: 8, padding: "4px 10px", fontSize: "11px", cursor: "pointer" }}
                >
                    Save
                </button>
                <button
                    onClick={() => handleCancel(index, field)}
                    className="btn btn-sm btn-light"
                    style={{ borderRadius: 8, padding: "4px 10px", fontSize: "11px", cursor: "pointer", border: "1px solid #ccc" }}
                >
                    Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="my-3" style={{ maxWidth: 490 }}>
      <div className="p-3">
        <div
          className="d-flex justify-content-between align-items-center mb-3"
          onClick={() => setOpen((prev) => !prev)}
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <div className="d-flex align-items-center">
            <span
              style={{ fontSize: "30px", color: "#b2a9ff", marginRight: "6px" }}
            >
              ⋮⋮
            </span>
            <h5 className="fw-bold m-0" style={{ color: purple }}>
              Recruitment Source(s)
            </h5>
            <span className="badge ms-2 rounded-pill bg-light text-dark border">
              {sourcesList.length}
            </span>
          </div>

          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              backgroundColor: purple,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "transform 0.3s ease",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
          >
            <i className="bi bi-chevron-right" style={{ fontSize: "14px" }} />
          </div>
        </div>

        {open && (
          <>
            {sourcesList.map((item, i) => (
              <div key={i} className="mb-4 position-relative">
                <div
                  style={{ position: "absolute", top: 0, right: 0, zIndex: 5 }}
                >
                  <FontAwesomeIcon
                    icon={faTrash}
                    style={{
                      cursor: "pointer",
                      fontSize: "16px",
                      color: purple,
                    }}
                    title="Remove Source"
                    onClick={() => handleRemove(i)}
                  />
                </div>

                {renderSelect(
                  i,
                  "Source Name",
                  "recruitment_id",
                  availableSources.map((s) => ({
                    value: s.id,
                    label: s.source_name,
                  })),
                  item.recruitment_id,
                )}

                {i < sourcesList.length - 1 && (
                  <hr className="my-4" style={{ borderColor: "#eee" }} />
                )}
              </div>
            ))}

            <div className="text-center mt-3">
              <button
                onClick={handleAdd}
                className="btn btn-sm"
                style={{
                  border: `1px dashed ${purple}`,
                  color: purple,
                  borderRadius: "20px",
                  padding: "5px 20px",
                  backgroundColor: "transparent",
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Another Source
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecruitmentSourceSection;